import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { inbox } from '@repletoryreach/sdk-api';
import { Card, Stack, Title, Text, Badge, Group, Skeleton } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../../../i18n/I18nProvider';
import { formatDateTime, formatRelative } from '../../../../lib/format';

const MUTED = 'var(--rr-color-text-muted, #94a3b8)';

/** Badge colour per conversation state. */
const STATE_COLOUR: Record<string, string> = {
  open: 'blue',
  pending: 'orange',
  resolved: 'green',
  archived: 'gray'
};

export function ConversationsTab({ msisdn }: { msisdn: string }) {
  const t = useT();
  // W46: `search` is now bound server-side, so the msisdn narrows the query
  // instead of us pulling a 200-row window. It is a substring match, so the
  // exact-match filter below stays as the precision step.
  const { data, isLoading } = useQuery({
    queryKey: ['conversations', 'byMsisdn', msisdn],
    queryFn: () => inbox.listConversations({ limit: 50, search: msisdn })
  });
  const matches = useMemo(
    () => (data?.data ?? []).filter((c) => c.contact_msisdn === msisdn),
    [data, msisdn]
  );

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Title order={4}>{t('contacts.view.tabs.conversations')}</Title>
        <Text fz="xs" c={MUTED}>
          {t('contacts.view.conversations.hint', { msisdn })}
        </Text>
        {isLoading ? (
          <Stack gap="xs">
            <Skeleton height={18} />
            <Skeleton height={18} />
            <Skeleton height={18} />
          </Stack>
        ) : matches.length === 0 ? (
          <Text fz="sm" c={MUTED}>{t('contacts.view.conversations.empty')}</Text>
        ) : (
          <Stack gap={6}>
            {matches.map((conv) => (
              <Group key={conv.id} gap="sm" align="baseline" wrap="nowrap">
                {/*
                  §4 item 1: this rendered `state_id`, a UUID, as the badge
                  label, and compared it against the strings 'open'/'closed',
                  which a UUID never equals, so every conversation was orange
                  and every badge read "3f2a…". `state_code` is the field that
                  carries the meaning, and it is on the same row.
                */}
                <Badge size="xs" variant="light" color={STATE_COLOUR[conv.state_code ?? 'open'] ?? 'gray'}>
                  {t(`inbox.state.${conv.state_code ?? 'open'}`, conv.state_code ?? '')}
                </Badge>
                {/* §4 item 13: this was a nine-property inline style object. */}
                <Link to={`/inbox?conversation=${conv.id}`} className="rr-contact-view__thread">
                  {conv.last_message_preview ?? t('contacts.view.conversations.noPreview')}
                </Link>
                {conv.unread_count > 0 && (
                  <Badge size="xs" color="red" variant="filled">{conv.unread_count}</Badge>
                )}
                {/* §4 item 17: `title` is invisible on touch. */}
                <Tooltip label={formatDateTime(conv.last_message_at)} position="left">
                  <span className="rr-contact-view__when">{formatRelative(conv.last_message_at)}</span>
                </Tooltip>
              </Group>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
